import Add from '@mui/icons-material/Add';
import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { RutterItemizableType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useMemo, useRef, useState } from 'react';

import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { RutterItem } from '~hooks/useRutter/models';
import { formatLineItemTypeLabel } from '~pages/Settings/Integrations/utils/lineItemType';
import AddOnForm, {
  AddOnFormHandler,
} from '~pages/Settings/RatesManagement/AddOnCharges/components/AddOnForm';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  MAPPING_COLUMN_MIN_WIDTH_PX,
  MAPPING_SELECT_WIDTH_PX,
} from './mappingColumnWidth';
import MappingSelect, { QuickBooksOption } from './MappingSelect';
import MappingTabToolbar, { MappingStatusFilterValue } from './MappingTabToolbar';
import { useQuickBooksMappingQueries } from './QuickBooksMappingQueryContext';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

function toQuickBooksItemOption(item: RutterItem): QuickBooksOption {
  return { id: item.id, name: item.rutterApiItem?.name ?? '' };
}

function compareOptionNames(optionA: QuickBooksOption, optionB: QuickBooksOption) {
  return optionA.name.localeCompare(optionB.name);
}

interface ItemMappingRow {
  id: string;
  itemizableId: string;
  itemizableType: RutterItemizableType;
  name: string;
}

export default function ItemsTab() {
  const {
    addOns,
    coreLineItemTypes,
    isLoading,
    isManagedByOwner,
    itemMappings,
    qboItems,
    refetchAddOns,
    saveItemMapping,
  } = useQuickBooksMappingQueries();
  const { toasterStore, userStore } = useStores();
  const { createAddOnByCompanyId, isCreating } = useAddOns();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const addOnFormRef = useRef<AddOnFormHandler>({});
  const companyId = getEffectiveUserCompanyId(userStore);

  const mappingsByItemizableKey = useMemo(() => {
    return new Map(
      itemMappings.map((mapping) => {
        return [
          `${mapping.rutterItemizable.type}:${mapping.rutterItemizable.id}`,
          mapping,
        ];
      }),
    );
  }, [itemMappings]);

  const qboItemsById = useMemo(() => {
    return new Map(qboItems.map((item) => [item.id, item]));
  }, [qboItems]);

  // many-to-one is allowed here, so mapped QuickBooks items are NOT removed from other
  // rows' dropdowns; only new selections are limited to active, non-Category items.
  // Built once per item change rather than per row, since the table renders a selector
  // for every row at once.
  const selectableOptions = useMemo(() => {
    return qboItems
      .filter((item) => item.isSelectable)
      .map(toQuickBooksItemOption)
      .sort(compareOptionNames);
  }, [qboItems]);

  // a row's existing mapping stays selectable/visible even when the item no longer is
  const optionsForRow = useCallback(
    (row: ItemMappingRow) => {
      const rowMappingItemId = mappingsByItemizableKey.get(
        `${row.itemizableType}:${row.itemizableId}`,
      )?.rutterItemId;
      const mappedItem = rowMappingItemId
        ? qboItemsById.get(rowMappingItemId)
        : undefined;

      if (!mappedItem || mappedItem.isSelectable) {
        return selectableOptions;
      }

      return [...selectableOptions, toQuickBooksItemOption(mappedItem)].sort(
        compareOptionNames,
      );
    },
    [mappingsByItemizableKey, qboItemsById, selectableOptions],
  );

  const coreLineRows: ItemMappingRow[] = useMemo(() => {
    return coreLineItemTypes.map((lineItemType) => ({
      id: `${RutterItemizableType.LINE_ITEM_TYPE}:${lineItemType.id}`,
      itemizableId: lineItemType.id,
      itemizableType: RutterItemizableType.LINE_ITEM_TYPE,
      name: formatLineItemTypeLabel(lineItemType),
    }));
  }, [coreLineItemTypes]);

  const addOnRows: ItemMappingRow[] = useMemo(() => {
    return addOns.flatMap((addOn) => {
      const addOnId = addOn.id;
      if (!addOnId) {
        return [];
      }

      return [
        {
          id: `${RutterItemizableType.ADD_ON}:${addOnId}`,
          itemizableId: addOnId,
          itemizableType: RutterItemizableType.ADD_ON,
          name: addOn.name,
        },
      ];
    });
  }, [addOns]);

  const filterRows = useCallback(
    (rowsToFilter: ItemMappingRow[]) => {
      const normalizedSearchQuery = searchQuery.trim().toLowerCase();

      return rowsToFilter.filter((row) => {
        const matchesSearch =
          !normalizedSearchQuery ||
          row.name.toLowerCase().includes(normalizedSearchQuery);
        const rowStatus = mappingsByItemizableKey.has(
          `${row.itemizableType}:${row.itemizableId}`,
        )
          ? MappingStatusFilterValue.MAPPED
          : MappingStatusFilterValue.UNMAPPED;
        const matchesStatusFilter =
          statusFilter.length === 0 || statusFilter.includes(rowStatus);

        return matchesSearch && matchesStatusFilter;
      });
    },
    [searchQuery, statusFilter, mappingsByItemizableKey],
  );

  const mappedCount = useMemo(() => {
    return [...coreLineRows, ...addOnRows].filter((row) => {
      return mappingsByItemizableKey.has(`${row.itemizableType}:${row.itemizableId}`);
    }).length;
  }, [coreLineRows, addOnRows, mappingsByItemizableKey]);

  const handleSave = useCallback(
    async (row: ItemMappingRow, option: Nullable<QuickBooksOption>) => {
      const saved = await saveItemMapping(
        row.itemizableType,
        row.itemizableId,
        option?.id ?? null,
      );

      if (saved) {
        toasterStore.push(
          alert(
            t(`${I18N_PREFIX}.grid.tread_name_updated`, { name: row.name }),
            AlertTypes.success,
          ),
        );
      }

      return saved;
    },
    [saveItemMapping],
  );

  // the owner's connection maps line item types; the child's own add-ons stay editable
  const isRowLockedByOwner = useCallback(
    (row: ItemMappingRow) =>
      isManagedByOwner && row.itemizableType === RutterItemizableType.LINE_ITEM_TYPE,
    [isManagedByOwner],
  );

  const columns: Array<DataTableColumn<ItemMappingRow>> = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t(`${I18N_PREFIX}.grid.name`),
        valueGetter: (row) => row.name,
      },
      {
        field: 'mapping',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.quick_books_item`),
        minWidth: MAPPING_COLUMN_MIN_WIDTH_PX,
        renderCell: (row) => {
          const mapping = mappingsByItemizableKey.get(
            `${row.itemizableType}:${row.itemizableId}`,
          );
          const mappedItem = mapping ? qboItemsById.get(mapping.rutterItemId) : null;

          return (
            <MappingSelect
              dataTestId={`quick-books-mapping-select-${row.id}`}
              disabled={isRowLockedByOwner(row)}
              onSave={(option) => handleSave(row, option)}
              options={optionsForRow(row)}
              width={MAPPING_SELECT_WIDTH_PX}
              value={
                mappedItem
                  ? { id: mappedItem.id, name: mappedItem.rutterApiItem?.name ?? '' }
                  : null
              }
            />
          );
        },
      },
    ];
  }, [
    isRowLockedByOwner,
    mappingsByItemizableKey,
    qboItemsById,
    optionsForRow,
    handleSave,
  ]);

  const closeCreateDialog = useCallback(() => setIsCreateDialogOpen(false), []);

  const handleAddOnFormSubmit = useCallback(async () => {
    const data = await addOnFormRef.current?.onSubmit?.();
    if (!data) return;

    await createAddOnByCompanyId(companyId, data);
    toasterStore.push(
      alert(t('administration.rates.add_ons.add_on_created'), AlertTypes.success),
    );
    setIsCreateDialogOpen(false);
    refetchAddOns();
  }, [companyId, createAddOnByCompanyId, refetchAddOns]);

  return (
    <>
      <MappingTabToolbar
        actions={
          <SmallButton
            color="brandV2Yellow"
            onClick={() => setIsCreateDialogOpen(true)}
            startIcon={<Add />}
          >
            {t(`${I18N_PREFIX}.tread_first.new_add_on`)}
          </SmallButton>
        }
        mappedCount={mappedCount}
        onSearchQueryChange={setSearchQuery}
        onStatusFilterChange={setStatusFilter}
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        unmappedCount={coreLineRows.length + addOnRows.length - mappedCount}
      />

      <Typography sx={{ mb: 1 }} variant="h6">
        {t(`${I18N_PREFIX}.tread_first.core_lines`)}
      </Typography>
      <DataTable
        columns={columns}
        hideToolbar
        id="quick-books-mapping-core-lines-grid"
        loading={isLoading}
        rows={filterRows(coreLineRows)}
      />

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          {t(`${I18N_PREFIX}.tread_first.add_ons`)}
        </Typography>
        <DataTable
          columns={columns}
          hideToolbar
          id="quick-books-mapping-add-ons-grid"
          loading={isLoading}
          rows={filterRows(addOnRows)}
        />
      </Box>

      <Dialog
        isOpen={isCreateDialogOpen}
        onClose={closeCreateDialog}
        preventCloseOnBackdropClick
        sx={{ [`.${paperClasses.root}`]: { maxWidth: '700px' } }}
      >
        <DialogHeader onClose={closeCreateDialog}>
          {t('administration.rates.add_ons.create_add_on')}
        </DialogHeader>

        <DialogContent>
          <AddOnForm ref={addOnFormRef} />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isCreating}
            onClick={closeCreateDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={isCreating}
            loading={isCreating}
            onClick={handleAddOnFormSubmit}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
